import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useMerchantId } from './useMerchantId';
import type { SaleLike } from '@/lib/report-metrics';
import { FETCH_ALL_MAX_PAGES, FETCH_ALL_PER_PAGE } from '@/lib/fetch-all';


/**
 * Hisobotlar uchun savdolarni sale_items bilan yig'adi. `GET /sales`
 * paginatsiyalangan (Laravel default paginator) — `date_from` serverda
 * filtrlaydi, shuning uchun bu yerda faqat sahifalarni yig'ib chiqamiz.
 * SaleResource shaklini (items/branch/cashier, integer id) hisobot
 * kutubxonasi (`report-metrics.ts`) kutgan Supabase-uslubidagi
 * `SaleLike`/`sale_items` shakliga moslaymiz — o'sha faylni o'zgartirmasdan.
 */
export function useSalesWithItems(from: Date | null) {
  const { merchantId } = useMerchantId();
  const fromTs = from ? from.getTime() : null;

  return useQuery({
    queryKey: ['sales-with-items', merchantId, fromTs],
    enabled: !!merchantId,
    staleTime: 1000 * 60 * 2,
    queryFn: async () => {
      const all: SaleLike[] = [];
      for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page++) {
        const { data, error } = await apiClient.GET('/sales', {
          params: { query: { page, per_page: FETCH_ALL_PER_PAGE, date_from: from ? from.toISOString() : undefined } },
        });
        if (error) throw error;
        const sales = data?.data ?? [];
        for (const s of sales) {
          all.push({
            id: s.id != null ? String(s.id) : undefined,
            created_at: s.created_at ?? '',
            total_amount: s.total_amount,
            payment_method: s.payment_method,
            is_murabaha: s.is_murabaha,
            status: s.status,
            branch_id: s.branch_id != null ? String(s.branch_id) : null,
            cashier_id: s.cashier_id != null ? String(s.cashier_id) : null,
            sale_items: (s.items ?? []).map(i => ({
              product_id: i.product_id != null ? String(i.product_id) : null,
              product_name: i.product_name,
              // FR-95: kategoriya bo'yicha taqsimot (Lovable products.category)
              products: { name: i.product_name, category: i.product?.category ?? null },
              quantity: i.quantity,
              total: i.total,
            })),
          });
        }
        const lastPage = data?.meta?.last_page ?? 1;
        if (page >= lastPage) break;
      }
      return all;
    },
  });
}
